/**
 * Centralized Prompt Templates for AI Analysis
 * All prompts are provider-agnostic
 */

/**
 * CV Extraction Prompt Template
 * Extracts structured data from raw CV text
 */
export const CV_EXTRACTION_PROMPT = (cvText) => `
You are an expert resume parser for immigration assessments. Extract structured data from the following CV content.

CV CONTENT:
${cvText}

Respond ONLY with valid JSON using this schema:
{
  "personalInfo": {
    "name": "",
    "email": "",
    "phone": "",
    "location": ""
  },
  "education": [
    {
      "degree": "",
      "institution": "",
      "field": "",
      "year": "",
      "gpa": ""
    }
  ],
  "experience": [
    {
      "company": "",
      "position": "",
      "startDate": "",
      "endDate": "",
      "duration": "",
      "responsibilities": []
    }
  ],
  "skills": {
    "technical": [],
    "programming": [],
    "tools": [],
    "certifications": []
  },
  "summary": "",
  "strengths": [],
  "languageProficiency": {
    "english": "",
    "other": []
  },
  "assessment": {
    "score": 0,
    "reasoning": ""
  },
  "totalExperienceYears": "",
  "rawText": ""
}

Rules:
- Use strings for all textual fields.
- Populate arrays even if empty.
- Copy raw CV text into "rawText".
- No markdown, prose, or comments.
`.trim();

/**
 * Immigration Analysis Prompt Template
 * Provides detailed immigration pathway assessment
 */
export const IMMIGRATION_ANALYSIS_PROMPT = (userProfile, extractedData, cvText) => {
  const targetCountry = userProfile?.countrySelection?.selectedCountry || 'Germany';
  const selectedPathway = userProfile?.reviewData?.selectedPathway || 'Not specified';
  const wizardEducation = userProfile?.education?.highestEducation || 'Not specified';
  const wizardField = userProfile?.education?.fieldOfStudy || 'Not specified';
  const wizardExperiencePositions = Array.isArray(userProfile?.experience) ? userProfile.experience.length : 0;

  return `
You are the senior immigration strategist for Ravana Migration Services. Provide a deterministic, comprehensive CV analysis focused on ${targetCountry} ${selectedPathway} eligibility.

Candidate Wizard Selections:
- Target country: ${targetCountry}
- Selected pathway: ${selectedPathway}
- Immigration purpose: ${userProfile?.countrySelection?.answers?.purpose || userProfile?.countrySelection?.answers?.immigrationPurpose || 'Not specified'}
- Wizard education: ${wizardEducation} in ${wizardField}
- Wizard experience entries: ${wizardExperiencePositions}
- Language skills: ${JSON.stringify(userProfile?.languageSkills || {})}
- Financial capacity: ${JSON.stringify(userProfile?.financialInfo || {})}
- Age: ${userProfile?.personalInfo?.age || 'Not specified'}

Structured CV Extraction (from parser):
${JSON.stringify(extractedData, null, 2)}

Full CV Text (fallback reference):
${cvText}

OUTPUT INSTRUCTIONS:
- Return ONLY valid JSON. No markdown, code fences, or commentary.
- Populate every section with concrete, CV-backed insights.
- Mark missing information explicitly instead of guessing.

OUTPUT SHAPE:
{
  "educationAssessment": {
    "level": "",
    "institutions": [],
    "fields": [],
    "recognition": "",
    "relevance": "",
    "institutionQuality": "",
    "gpaStrength": "",
    "additionalEducation": []
  },
  "experienceAnalysis": {
    "positions": [
      {
        "title": "",
        "company": "",
        "duration": "",
        "responsibilities": [],
        "achievements": [],
        "relevance": "",
        "seniority": ""
      }
    ],
    "totalYears": "",
    "relevantExperience": "",
    "careerProgression": "",
    "industryDemand": "",
    "seniority": "",
    "companyReputation": "",
    "employmentGaps": "",
    "currentSalary": "",
    "leadershipExperience": ""
  },
  "skillsEvaluation": {
    "technicalSkills": {
      "programming": [],
      "frameworks": [],
      "software": [],
      "specializations": [],
      "inDemandSkills": []
    },
    "languageSkills": {
      "english": "",
      "german": "",
      "otherLanguages": [],
      "languageAdvantage": ""
    },
    "softSkills": [],
    "certifications": [],
    "professionalMemberships": []
  },
  "projectsAndAchievements": {
    "notableProjects": [],
    "awards": [],
    "publications": [],
    "speakingEngagements": []
  },
  "immigrationStrengths": [],
  "potentialConcerns": [],
  "recommendedPathways": [
    {
      "country": "",
      "pathway": "",
      "score": 0,
      "confidence": "",
      "reasoning": ""
    }
  ],
  "selectedPathwayAnalysis": {
    "pathway": "${selectedPathway}",
    "country": "${targetCountry}",
    "eligibilityScore": 0,
    "scoreBreakdown": {
      "education": 0,
      "experience": 0,
      "language": 0,
      "financialReadiness": 0,
      "ageOptimality": 0
    },
    "eligible": "",
    "strengths": [],
    "weaknesses": [],
    "missingRequirements": [],
    "timelineEstimate": "",
    "successProbability": 0,
    "improvementActions": []
  },
  "alternativePathways": [],
  "salaryExpectations": {
    "currentSalary": "",
    "targetCountrySalary": "",
    "competitiveness": ""
  },
  "missingCriticalInfo": [],
  "overallAssessment": "",
  "recommendedNextSteps": [],
  "documentChecklist": [],
  "visaReadiness": {
    "status": "",
    "reasoning": "",
    "priorityActions": []
  }
}

SCORING RULES (apply even if data missing):
- Education score (0-25): ${getEducationScoringRules(targetCountry)}
- Experience score (0-25): ${getExperienceScoringRules(selectedPathway)}
- Language score (0-25): IELTS 6.5+=25, 6.0-6.4=20, 5.5-5.9=15, 5.0-5.4=10, <5.0=5
- Financial readiness (0-15): Budget covers 100%=15, 75%=12, 50%=8, <50%=5
- Age score (0-10): 18-25=10, 26-30=9, 31-35=7, 36-40=5, 41+=3

Must Do:
- Prioritise ${selectedPathway} in ${targetCountry}. Alternatives only if 20+ points stronger.
- Reference explicit CV evidence for every claim.
- Mark missing data clearly and suggest exact documents/tests required.
- Keep tone professional, data-backed, action-oriented.
`.trim();
};

/**
 * Get education scoring rules based on target country
 */
const getEducationScoringRules = (targetCountry) => {
  const country = targetCountry?.toLowerCase() || '';

  if (country === 'germany') {
    return 'Bachelor=20, Master=25, PhD=25, A/L 3 passes=15 for study pathways';
  } else if (country === 'uk') {
    return 'Master=25, Bachelor=22, Foundation eligible=15, O/L for Foundation=10';
  } else {
    return 'Recognized degree=25, Diploma=20, Certificate eligible=15';
  }
};

/**
 * Get experience scoring rules based on pathway
 */
const getExperienceScoringRules = (selectedPathway) => {
  const pathway = selectedPathway?.toLowerCase() || '';

  if (pathway.includes('study')) {
    return '0-2 years=10, 2-5 years=15, 5+ years=20 (study pathways value experience but it is supportive)';
  } else {
    return '0-2 years=5, 2-5 years=15, 5+ years=25 (work pathways need strong experience)';
  }
};

export default {
  CV_EXTRACTION_PROMPT,
  IMMIGRATION_ANALYSIS_PROMPT
};
